<template>
  <div class="max-w-7xl mx-auto p-6 bg-gray-50">
    <div class="mb-4 flex items-center gap-3">
      <i class="fa-solid fa-layer-group text-blue-600"></i>
      <h1 class="text-xl font-semibold text-gray-700">
        Maturidade por Domínio
      </h1>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <div
        v-for="d in domains"
        :key="d.key"
        class="card bg-white rounded-2xl shadow-lg p-6 flex flex-col justify-between overflow-hidden relative"
      >
        <!-- Barra vertical de progresso -->
        <div
          class="absolute left-0 top-0 h-1 rounded-b-lg"
          :style="{
            width: `${isLoading ? loadingProgress : calcularMaturidade(d)}%`,
            backgroundColor: colorMap[d.color].border,
            transition: 'width 0.5s ease',
          }"
        ></div>

        <div>
          <div class="flex items-center justify-center gap-3 mb-3">
            <span
              class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white"
              :style="{ color: colorMap[d.color].text, fontSize: '18px' }"
            >
              <i :class="['fa-solid', d.icon]"></i>
            </span>
            <h3 class="card-title text-gray-700 text-base">{{ d.title }}</h3>
          </div>

          <div class="flex justify-center gap-8 mt-3 text-center">
            <div>
              <div class="value-number">
                <AnimatedNumber
                  :value="d.implemented"
                  :is-loading="isLoading"
                />
              </div>
              <div class="small-label">Implement.</div>
            </div>
            <div>
              <div class="value-number">
                <AnimatedNumber :value="d.partial" :is-loading="isLoading" />
              </div>
              <div class="small-label">Parciais</div>
            </div>
            <div>
              <div class="value-number">
                <AnimatedNumber
                  :value="d.notImplemented"
                  :is-loading="isLoading"
                />
              </div>
              <div class="small-label">Não Impl.</div>
            </div>
          </div>
        </div>

        <div class="mt-5">
          <div class="bg-gray-50 rounded-md py-3 px-4 text-center">
            <span
              class="text-xl font-bold"
              :style="{ color: colorMap[d.color].text }"
            >
              <AnimatedNumber
                :value="calcularMaturidade(d)"
                :is-loading="isLoading"
              />%
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, watch } from 'vue'
import { getSIDomainsByIdEnterprise } from '@/services/graphs/api.domains_si.js'
import AnimatedNumber from '@/components/ui/AnimatedNumber.vue'

const props = defineProps({
  enterpriseId: { type: String, required: true },
})

const domains = ref([])
const isLoading = ref(true)
const loadingProgress = ref(0)
let progressInterval = null

const colorMap = {
  blue: { border: '#2b82d9', text: '#2b82d9' },
  green: { border: '#16a34a', text: '#16a34a' },
  red: { border: '#ef4444', text: '#ef4444' },
  yellow: { border: '#f59e0b', text: '#f59e0b' },
}

function calcularMaturidade(d) {
  const implementados = d.implemented || 0
  const parciais = d.partial || 0
  const naoImplementados = d.notImplemented || 0
  const total = implementados + parciais + naoImplementados

  if (total === 0) return 0

  const score = (implementados * 1 + parciais * 0.5) / total
  return parseFloat((score * 100).toFixed(1))
}

function createDomainData(
  title,
  icon,
  color,
  implemented,
  partial,
  notImplemented
) {
  return {
    title,
    icon,
    color,
    implemented: implemented || 0,
    partial: partial || 0,
    notImplemented: notImplemented || 0,
  }
}

function startLoadingAnimation() {
  isLoading.value = true
  loadingProgress.value = 0
  clearInterval(progressInterval)

  progressInterval = setInterval(() => {
    if (loadingProgress.value < 90) {
      loadingProgress.value += 5
    }
  }, 100)
}

function stopLoadingAnimation() {
  loadingProgress.value = 100
  setTimeout(() => {
    clearInterval(progressInterval)
    isLoading.value = false
    loadingProgress.value = 0
  }, 500)
}

async function fetchDomains(id) {
  if (!id) {
    domains.value = []
    stopLoadingAnimation()
    return
  }

  startLoadingAnimation()

  try {
    const data = await getSIDomainsByIdEnterprise(id)
    const item = Array.isArray(data) ? data[0] : data

    domains.value = [
      createDomainData(
        'Governança e Ecossistema',
        'fa-landmark',
        'blue',
        item?.DomainsGovernanceEcosystemImplemented,
        item?.DomainsGovernanceEcosystemPartiallyImplemented,
        item?.DomainsGovernanceEcosystemNotImplemented
      ),
      createDomainData(
        'Proteção',
        'fa-shield-alt',
        'green',
        item?.DomainsProtectionImplemented,
        item?.DomainsProtectionPartiallyImplemented,
        item?.DomainsProtectionNotImplemented
      ),
      createDomainData(
        'Defesa',
        'fa-fighter-jet',
        'red',
        item?.DomainsDefenseImplemented,
        item?.DomainsDefensePartiallyImplemented,
        item?.DomainsDefenseNotImplemented
      ),
      createDomainData(
        'Resiliência',
        'fa-sync-alt',
        'yellow',
        item?.DomainsResilienceImplemented,
        item?.DomainsResiliencePartiallyImplemented,
        item?.DomainsResilienceNotImplemented
      ),
    ]
  } catch (err) {
    console.error('Erro ao buscar domínios:', err)
    domains.value = [
      createDomainData(
        'Governança e Ecossistema',
        'fa-landmark',
        'blue',
        0,
        0,
        0
      ),
      createDomainData('Proteção', 'fa-shield-alt', 'green', 0, 0, 0),
      createDomainData('Defesa', 'fa-fighter-jet', 'red', 0, 0, 0),
      createDomainData('Resiliência', 'fa-sync-alt', 'yellow', 0, 0, 0),
    ]
  } finally {
    stopLoadingAnimation()
  }
}

watch(
  () => props.enterpriseId,
  (id) => fetchDomains(id),
  { immediate: true }
)

onMounted(() => {
  if (props.enterpriseId) {
    fetchDomains(props.enterpriseId)
  }
})
</script>
